import React, { memo, useEffect, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { TablePagination, Tooltip } from '@material-ui/core';
import {
    countUserAssignments,
    getCellState,
    getLicenceTypeLabel,
    getUserDisplayName,
    isAssigned,
} from './assignmentModel';

const useStyles = makeStyles(theme => ({
    frame: {
        border: '1px solid var(--dm-border, #e1e7ee)',
        borderRadius: 10,
        overflow: 'hidden',
        backgroundColor: 'var(--contentColor, #ffffff)',
        boxShadow: '0 6px 20px rgba(30, 48, 70, 0.05)',
    },
    scroller: {
        maxHeight: 520,
        overflow: 'auto',
    },
    table: {
        borderCollapse: 'separate',
        borderSpacing: 0,
        minWidth: '100%',
        fontSize: '0.75rem',
    },
    corner: {
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: 3,
        minWidth: 260,
        maxWidth: 260,
        padding: theme.spacing(1.25, 1.5),
        textAlign: 'left',
        verticalAlign: 'bottom',
        backgroundColor: 'var(--dm-row-alt, #f8fafc)',
        borderBottom: '1px solid var(--dm-border, #e1e7ee)',
        borderRight: '1px solid var(--dm-border, #e1e7ee)',
        color: 'var(--dm-text-strong, #263548)',
        fontWeight: 700,
    },
    cornerHint: {
        display: 'block',
        marginTop: 2,
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.6875rem',
        fontWeight: 500,
    },
    colHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        width: 136,
        minWidth: 136,
        maxWidth: 136,
        padding: theme.spacing(1, 1),
        textAlign: 'center',
        verticalAlign: 'bottom',
        backgroundColor: 'var(--dm-row-alt, #f8fafc)',
        borderBottom: '1px solid var(--dm-border, #e1e7ee)',
        borderRight: '1px solid var(--dm-border, #eef1f5)',
    },
    colTitle: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        minHeight: 32,
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.75rem',
        fontWeight: 700,
        lineHeight: 1.35,
        overflowWrap: 'anywhere',
    },
    colMeta: {
        marginTop: 4,
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.625rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    },
    capacity: {
        display: 'inline-block',
        marginTop: 4,
        padding: '1px 7px',
        borderRadius: 999,
        color: '#1f4f91',
        backgroundColor: 'rgba(61, 125, 221, 0.1)',
        fontSize: '0.6875rem',
        fontWeight: 700,
    },
    capacityFull: {
        color: '#9b2c2c',
        backgroundColor: 'rgba(197, 48, 48, 0.1)',
    },
    rowHeader: {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        minWidth: 260,
        maxWidth: 260,
        padding: theme.spacing(0.75, 1.5),
        textAlign: 'left',
        fontWeight: 400,
        backgroundColor: 'var(--contentColor, #ffffff)',
        borderBottom: '1px solid var(--dm-border, #eef1f5)',
        borderRight: '1px solid var(--dm-border, #e1e7ee)',
    },
    rowHeaderInner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        minWidth: 0,
    },
    userEmail: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.8125rem',
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    userMeta: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.6875rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    userText: {
        minWidth: 0,
    },
    cell: {
        padding: 0,
        textAlign: 'center',
        borderBottom: '1px solid var(--dm-border, #eef1f5)',
        borderRight: '1px solid var(--dm-border, #f2f4f7)',
        transition: 'background-color 120ms ease',
    },
    cellAdded: {
        backgroundColor: 'rgba(40, 122, 75, 0.14)',
    },
    cellRemoved: {
        backgroundColor: 'rgba(197, 48, 48, 0.12)',
    },
    cellLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 40,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(61, 125, 221, 0.07)',
        },
    },
    cellLabelDisabled: {
        cursor: 'not-allowed',
        opacity: 0.45,
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    checkbox: {
        width: 17,
        height: 17,
        margin: 0,
        cursor: 'inherit',
        accentColor: theme.palette.primary.main,
    },
    row: {
        '&:hover $rowHeader': {
            backgroundColor: 'var(--dm-row-alt, #f6f9fc)',
        },
    },
    legend: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(1, 1.5),
        borderTop: '1px solid var(--dm-border, #e1e7ee)',
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.6875rem',
    },
    legendItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
    },
    swatch: {
        width: 12,
        height: 12,
        borderRadius: 3,
        border: '1px solid var(--dm-border, #d6dde6)',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        borderTop: '1px solid var(--dm-border, #e1e7ee)',
    },
    pagination: {
        marginLeft: 'auto',
        '& .MuiTablePagination-toolbar': {
            minHeight: 44,
        },
    },
    empty: {
        padding: theme.spacing(5, 2),
        textAlign: 'center',
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
    },
}));

const TriStateCheckbox = ({ checked, indeterminate, className, ...props }) => {
    const ref = useRef(null);
    // Native clicks reset `indeterminate`, so it is re-applied after every render.
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
    });
    return <input ref={ref} type="checkbox" checked={checked} className={className} {...props} />;
};

const MatrixCell = memo(({ licenceKey, licenceName, userId, userEmail, state, disabled, onToggle, classes }) => {
    const checked = state === 'assigned' || state === 'added';
    const cellClass = [
        classes.cell,
        state === 'added' && classes.cellAdded,
        state === 'removed' && classes.cellRemoved,
    ].filter(Boolean).join(' ');
    return (
        <td className={cellClass}>
            <label className={`${classes.cellLabel}${disabled ? ` ${classes.cellLabelDisabled}` : ''}`} title={disabled ? 'No licences left' : undefined}>
                <input
                    type="checkbox"
                    className={classes.checkbox}
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onToggle(licenceKey, userId)}
                    aria-label={`${licenceName} for ${userEmail}`}
                />
            </label>
        </td>
    );
});

const AssignmentMatrix = ({
    users,
    filteredUserIds,
    licences,
    baseline,
    draft,
    remainingByKey,
    onToggle,
    onSetColumn,
    onSetRow,
    totalUsers,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    emptyMessage,
}) => {
    const classes = useStyles();
    const licenceKeys = useMemo(() => licences.map(licence => licence.key), [licences]);

    const columnState = useMemo(() => licences.reduce((items, licence) => {
        const assignedCount = filteredUserIds.filter(userId => isAssigned(draft, licence.key, userId)).length;
        items[licence.key] = {
            checked: filteredUserIds.length > 0 && assignedCount === filteredUserIds.length,
            indeterminate: assignedCount > 0 && assignedCount < filteredUserIds.length,
        };
        return items;
    }, {}), [licences, filteredUserIds, draft]);

    if (emptyMessage) {
        return <div className={`${classes.frame} ${classes.empty}`}>{emptyMessage}</div>;
    }

    return (
        <div className={classes.frame}>
            <div className={classes.scroller}>
                <table className={classes.table}>
                    <thead>
                        <tr>
                            <th className={classes.corner} scope="col">
                                Users
                                <span className={classes.cornerHint}>
                                    Tick a row to give a user every visible licence; tick a column header to give a licence to every filtered user.
                                </span>
                            </th>
                            {licences.map((licence) => {
                                const remaining = remainingByKey[licence.key] ?? licence.remaining;
                                const state = columnState[licence.key] || {};
                                const full = remaining <= 0;
                                return (
                                    <th key={licence.key} className={classes.colHeader} scope="col">
                                        <Tooltip title={licence.provider ? `${licence.name} (${licence.provider})` : licence.name} placement="top">
                                            <div className={classes.colTitle}>{licence.name}</div>
                                        </Tooltip>
                                        <div className={classes.colMeta}>{getLicenceTypeLabel(licence.type)}</div>
                                        <span className={`${classes.capacity}${full ? ` ${classes.capacityFull}` : ''}`}>
                                            {Math.max(0, remaining)} of {licence.allocated} left
                                        </span>
                                        <div style={{ marginTop: 6 }}>
                                            <TriStateCheckbox
                                                className={classes.checkbox}
                                                checked={Boolean(state.checked)}
                                                indeterminate={state.indeterminate}
                                                disabled={!filteredUserIds.length || (full && !state.checked && !state.indeterminate)}
                                                onChange={() => onSetColumn(licence.key, !state.checked)}
                                                aria-label={state.checked
                                                    ? `Remove ${licence.name} from all filtered users`
                                                    : `Assign ${licence.name} to all filtered users`}
                                                title={state.checked ? 'Remove from all filtered users' : 'Assign to all filtered users'}
                                            />
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => {
                            const assignedVisible = licenceKeys.filter(key => isAssigned(draft, key, user.uuid)).length;
                            const rowChecked = licenceKeys.length > 0 && assignedVisible === licenceKeys.length;
                            const name = getUserDisplayName(user);
                            const total = countUserAssignments(draft, user.uuid);
                            return (
                                <tr key={user.uuid} className={classes.row}>
                                    <th className={classes.rowHeader} scope="row">
                                        <div className={classes.rowHeaderInner}>
                                            <TriStateCheckbox
                                                className={classes.checkbox}
                                                checked={rowChecked}
                                                indeterminate={assignedVisible > 0 && !rowChecked}
                                                disabled={!licenceKeys.length}
                                                onChange={() => onSetRow(user.uuid, !rowChecked)}
                                                aria-label={rowChecked
                                                    ? `Remove all visible licences from ${user.email}`
                                                    : `Assign all visible licences to ${user.email}`}
                                                title={rowChecked ? 'Remove all visible licences' : 'Assign all visible licences'}
                                            />
                                            <div className={classes.userText}>
                                                <div className={classes.userEmail} title={user.email}>{user.email || '-'}</div>
                                                <div className={classes.userMeta}>
                                                    {[name, `${total} licence${total === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                                                </div>
                                            </div>
                                        </div>
                                    </th>
                                    {licences.map((licence) => {
                                        const state = getCellState(baseline, draft, licence.key, user.uuid);
                                        const remaining = remainingByKey[licence.key] ?? licence.remaining;
                                        return (
                                            <MatrixCell
                                                key={licence.key}
                                                licenceKey={licence.key}
                                                licenceName={licence.name}
                                                userId={user.uuid}
                                                userEmail={user.email}
                                                state={state}
                                                disabled={(state === 'none' || state === 'removed') && remaining <= 0}
                                                onToggle={onToggle}
                                                classes={classes}
                                            />
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className={classes.footer}>
                <div className={classes.legend}>
                    <span className={classes.legendItem}>
                        <span className={`${classes.swatch} ${classes.cellAdded}`} /> To be assigned
                    </span>
                    <span className={classes.legendItem}>
                        <span className={`${classes.swatch} ${classes.cellRemoved}`} /> To be removed
                    </span>
                    <span className={classes.legendItem}>
                        <span className={`${classes.capacity} ${classes.capacityFull}`} style={{ marginTop: 0 }}>0 left</span>
                        No capacity in the project allocation
                    </span>
                </div>
                <TablePagination
                    component="div"
                    className={classes.pagination}
                    count={totalUsers}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                    labelRowsPerPage="Users per page"
                />
            </div>
        </div>
    );
};

export default AssignmentMatrix;
